import { useCallback, useEffect, useState, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { ImagePlus, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { logError } from "@/lib/structured-log";
import {
  getWorkspaceCompanyProfileServer,
  previewWorkspaceCompanyProfileServer,
  saveWorkspaceCompanyProfileServer,
  type WorkspaceCompanyProfile,
  type WorkspaceCompanyProfileInput,
} from "@/server/workspace-company-profile";

export const Route = createFileRoute("/workspace/$workspaceSlug/campaigns/setup/company-profile")({
  loader: async ({ parentMatchPromise }) => {
    const parent = await parentMatchPromise;
    const workspaceId = parent?.loaderData?.workspaceId;
    if (!workspaceId || typeof workspaceId !== "string") {
      throw new Error("workspaceId not found in parent loader");
    }
    return { workspaceId };
  },
  component: CampaignCompanyProfileSetupPage,
});

const companyProfileFormSchema = z.object({
  name: z.string(),
  website: z.string(),
  location: z.string(),
  foundedIn: z.string(),
  businessType: z.string(),
  description: z.string(),
  targetAudience: z.string(),
  sellingPointsText: z.string(),
});

type CompanyProfileFormValues = z.infer<typeof companyProfileFormSchema>;

const EMPTY_FORM_VALUES: CompanyProfileFormValues = {
  name: "",
  website: "",
  location: "",
  foundedIn: "",
  businessType: "",
  description: "",
  targetAudience: "",
  sellingPointsText: "",
};

function CampaignCompanyProfileSetupPage() {
  const { workspaceId } = Route.useLoaderData();
  const { workspaceSlug } = Route.useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["pages", "common"]);
  const [websiteUrl, setWebsiteUrl] = useState<string>("");
  const [profile, setProfile] = useState<WorkspaceCompanyProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);

  const form = useForm<CompanyProfileFormValues>({
    resolver: zodResolver(companyProfileFormSchema),
    defaultValues: EMPTY_FORM_VALUES,
  });

  const navigateToCampaigns = useCallback(async (): Promise<void> => {
    await navigate({
      to: "/workspace/$workspaceSlug/campaigns",
      params: { workspaceSlug },
    });
  }, [navigate, workspaceSlug]);

  useEffect(() => {
    let isMounted = true;

    async function loadProfile(): Promise<void> {
      setIsLoadingProfile(true);
      try {
        const savedProfile = await getWorkspaceCompanyProfileServer({
          data: { workspaceId },
        });

        if (!isMounted) return;

        if (savedProfile) {
          await navigateToCampaigns();
          return;
        }
      } catch (error) {
        logError("campaigns.company_profile_setup.load_failed", {
          workspace_id: workspaceId,
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false);
        }
      }
    }

    void loadProfile();

    return () => {
      isMounted = false;
    };
  }, [navigateToCampaigns, workspaceId]);

  const handleGenerate = async (): Promise<void> => {
    const normalizedWebsite = websiteUrl.trim();
    if (!normalizedWebsite || isGenerating) return;

    setIsGenerating(true);
    try {
      const draft = await previewWorkspaceCompanyProfileServer({
        data: {
          workspaceId,
          website: normalizedWebsite,
          language: i18n.language,
        },
      });
      setProfile(draft);
      setWebsiteUrl(draft.website);
      form.reset(toFormValues(draft));
      setShowForm(true);
      toast.success(t("pages:knowledge.companyProfiles.generated"));
    } catch (error) {
      logError("campaigns.company_profile_setup.generate_failed", {
        workspace_id: workspaceId,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("pages:knowledge.companyProfiles.errors.generateFailed"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async (values: CompanyProfileFormValues): Promise<void> => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await saveWorkspaceCompanyProfileServer({
        data: {
          workspaceId,
          profile: toSaveInput(values, profile),
        },
      });
      toast.success(t("pages:knowledge.companyProfiles.saved"));
      await navigateToCampaigns();
    } catch (error) {
      logError("campaigns.company_profile_setup.save_failed", {
        workspace_id: workspaceId,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("pages:knowledge.companyProfiles.errors.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!showForm) {
    return (
      <div className="flex h-full flex-col bg-white">
        <div className="flex flex-1 items-start justify-center px-6 pt-16">
          <div className="w-full max-w-[620px]">
            <div className="mb-10 text-center">
              <h2 className="text-[18px] font-semibold text-gray-900">
                {t("pages:knowledge.companyProfiles.entryTitle")}
              </h2>
              <p className="mt-4 text-sm text-gray-400">
                {t("pages:knowledge.companyProfiles.entryDescription")}
              </p>
            </div>

            <label
              className="mb-2 block text-sm font-medium text-gray-900"
              htmlFor="campaign-company-website"
            >
              {t("pages:knowledge.companyProfiles.websiteQuestion")}
            </label>
            <Input
              id="campaign-company-website"
              className="h-14 rounded-lg"
              placeholder={t("pages:knowledge.companyProfiles.websitePlaceholder")}
              value={websiteUrl}
              onChange={(event) => setWebsiteUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleGenerate();
                }
              }}
            />
            <div className="mt-3 flex items-center justify-end">
              <Button
                type="button"
                disabled={!websiteUrl.trim() || isGenerating}
                onClick={() => void handleGenerate()}
              >
                {isGenerating ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("pages:knowledge.companyProfiles.generating")}
                  </span>
                ) : (
                  <span>{t("pages:knowledge.companyProfiles.generate")}</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-white px-6 py-5">
      <form
        className="mx-auto w-full max-w-[1160px] space-y-4 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit(handleSave)(event);
        }}
      >
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-900">
            {t("pages:knowledge.companyProfiles.fields.logo")}
          </label>
          <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
            {profile?.logo ? (
              <img
                src={profile.logo}
                alt={t("pages:knowledge.companyProfiles.fields.logo")}
                className="h-full w-full rounded-lg object-contain"
              />
            ) : (
              <ImagePlus className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        <Field label={t("pages:knowledge.companyProfiles.fields.name")}>
          <Input {...form.register("name")} className="h-10" />
        </Field>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label={t("pages:knowledge.companyProfiles.fields.website")}>
            <Input {...form.register("website")} className="h-10" />
          </Field>
          <Field label={t("pages:knowledge.companyProfiles.fields.location")}>
            <Input {...form.register("location")} className="h-10" />
          </Field>
          <Field label={t("pages:knowledge.companyProfiles.fields.foundedIn")}>
            <Input {...form.register("foundedIn")} className="h-10" inputMode="numeric" />
          </Field>
          <Field label={t("pages:knowledge.companyProfiles.fields.businessType")}>
            <Input {...form.register("businessType")} className="h-10" />
          </Field>
        </div>

        <Field label={t("pages:knowledge.companyProfiles.fields.description")}>
          <Textarea {...form.register("description")} className="min-h-[96px] resize-none" />
        </Field>

        <Field label={t("pages:knowledge.companyProfiles.fields.targetAudience")}>
          <Textarea {...form.register("targetAudience")} className="min-h-[78px] resize-none" />
        </Field>

        <Field label={t("pages:knowledge.companyProfiles.fields.keyAdvantages")}>
          <Textarea {...form.register("sellingPointsText")} className="min-h-[78px] resize-none" />
        </Field>

        <div className="flex items-center justify-end pt-1">
          <Button type="submit" disabled={isSaving}>
            {isSaving ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("pages:knowledge.companyProfiles.saving")}
              </span>
            ) : (
              <span>{t("pages:knowledge.companyProfiles.update")}</span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: ReactNode;
  className?: string;
}

function Field({ label, children, className = "" }: FieldProps) {
  return (
    <div className={className}>
      <label className="mb-2 block text-sm font-medium text-gray-900">{label}</label>
      {children}
    </div>
  );
}

function toFormValues(profile: WorkspaceCompanyProfile): CompanyProfileFormValues {
  return {
    name: profile.name,
    website: profile.website,
    location: profile.location,
    foundedIn: profile.founded_in ? String(profile.founded_in) : "",
    businessType: profile.business_type,
    description: profile.description,
    targetAudience: profile.target_audience,
    sellingPointsText: profile.selling_points.join("\n"),
  };
}

function toSaveInput(
  values: CompanyProfileFormValues,
  profile: WorkspaceCompanyProfile | null,
): WorkspaceCompanyProfileInput {
  return {
    name: values.name,
    website: values.website,
    location: values.location,
    founded_in: parseFoundedIn(values.foundedIn),
    business_type: values.businessType,
    description: values.description,
    target_audience: values.targetAudience,
    selling_points: parseMultilineList(values.sellingPointsText),
    products: profile?.products ?? [],
    logo: profile?.logo ?? null,
  };
}

function parseFoundedIn(value: string): number | null {
  const normalized = value.trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseMultilineList(value: string): string[] {
  const seen = new Set<string>();
  const items: string[] = [];
  for (const item of value.split(/\r?\n/)) {
    const normalized = item.trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    items.push(normalized);
  }
  return items;
}
